import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useCreateInvite, type InviteRole } from '@/hooks/useInvites';
import { UserPlus } from 'lucide-react';
import { z } from 'zod';

const inviteSchema = z.object({
  email: z.string().email('Invalid email address'),
  role: z.enum(['admin', 'csr', 'affiliate', 'tech']),
  techId: z.string().uuid().nullable(),
}).refine((v) => (v.role === 'tech') === (v.techId !== null), {
  message: 'Pick the technician this login is for',
  path: ['techId'],
});

export const INVITE_ROLE_LABELS: Record<InviteRole, string> = {
  admin: 'Administrator',
  csr: 'Customer Service Rep',
  affiliate: 'Affiliate',
  tech: 'Technician (Steddy app)',
};

interface InviteUserDialogProps {
  /** Controlled mode: omit to render the default "Invite User" trigger button. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Pre-select a role (the Technicians page opens the dialog on role 'tech'). */
  defaultRole?: InviteRole;
  /** Pre-select the technician the app login is for (implies role 'tech'). */
  defaultTechId?: string | null;
  defaultEmail?: string;
}

/** Active technicians that do not have an app login yet: the only rows a tech invite may link to. */
function useInvitableTechs(enabled: boolean) {
  return useQuery({
    queryKey: ['techs', 'invitable'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('techs')
        .select('id, name, email')
        .eq('is_active', true)
        .is('user_id', null)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled,
  });
}

export function InviteUserDialog({
  open: controlledOpen,
  onOpenChange,
  defaultRole = 'csr',
  defaultTechId = null,
  defaultEmail = '',
}: InviteUserDialogProps) {
  const controlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!controlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const initialRole: InviteRole = defaultTechId ? 'tech' : defaultRole;
  const [email, setEmail] = useState(defaultEmail);
  const [role, setRole] = useState<InviteRole>(initialRole);
  const [techId, setTechId] = useState<string | null>(defaultTechId);
  const [error, setError] = useState<string | null>(null);
  const createInvite = useCreateInvite();
  const { data: techs = [] } = useInvitableTechs(open && role === 'tech');

  // Re-seed the form each time the dialog opens with (possibly new) defaults.
  useEffect(() => {
    if (!open) return;
    setEmail(defaultEmail);
    setRole(defaultTechId ? 'tech' : defaultRole);
    setTechId(defaultTechId);
    setError(null);
  }, [open, defaultEmail, defaultRole, defaultTechId]);

  // Picking a technician with an email on file fills the address in.
  const handleTechChange = (id: string) => {
    setTechId(id);
    const tech = techs.find((t) => t.id === id);
    if (tech?.email && !email) setEmail(tech.email);
  };

  const handleRoleChange = (value: string) => {
    const next = value as InviteRole;
    setRole(next);
    if (next !== 'tech') setTechId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = inviteSchema.safeParse({ email, role, techId });
    if (!parsed.success) {
      setError(parsed.error.errors[0]?.message ?? 'Check the form');
      return;
    }
    try {
      await createInvite.mutateAsync({ email: parsed.data.email, role: parsed.data.role, techId: parsed.data.techId });
      setEmail('');
      setRole('csr');
      setTechId(null);
      setOpen(false);
    } catch {
      // Toasted by the hook
    }
  };

  const selectedTech = techs.find((t) => t.id === techId);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
        <DialogTrigger asChild>
          <Button>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{role === 'tech' ? 'Invite technician to the Steddy app' : 'Invite New User'}</DialogTitle>
            <DialogDescription>
              {role === 'tech'
                ? 'The technician gets an email link to create their login. They sign in on the Steddy app, not the CRM.'
                : 'Send an invitation to a new team member. They will receive a magic link to set up their account.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={handleRoleChange}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csr">{INVITE_ROLE_LABELS.csr}</SelectItem>
                  <SelectItem value="admin">{INVITE_ROLE_LABELS.admin}</SelectItem>
                  <SelectItem value="affiliate">{INVITE_ROLE_LABELS.affiliate}</SelectItem>
                  <SelectItem value="tech">{INVITE_ROLE_LABELS.tech}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {role === 'tech' && (
              <div className="space-y-2">
                <Label htmlFor="tech">Technician</Label>
                <Select value={techId ?? ''} onValueChange={handleTechChange}>
                  <SelectTrigger id="tech">
                    <SelectValue placeholder="Choose the technician this login is for" />
                  </SelectTrigger>
                  <SelectContent>
                    {techs.length === 0 && (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        Every active technician already has a login or a pending invite.
                      </div>
                    )}
                    {techs.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}{t.email ? ` · ${t.email}` : ''}
                      </SelectItem>
                    ))}
                    {/* Keep a pre-selected tech visible even when the list has not loaded yet */}
                    {techId && !selectedTech && (
                      <SelectItem value={techId}>Selected technician</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only active technicians without an app login are listed. One login per technician.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={role === 'tech' ? 'tech@example.com' : 'user@example.com'}
                required
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createInvite.isPending}>
              {createInvite.isPending ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
